import Splide from '@splidejs/splide';
import { Build, Component, Element, Host, Prop, Watch, h } from '@stencil/core';
import { renderResponsiveImages } from '../../helpers/helpers';
import type {
  CssClassMap,
  Slider,
  SplideComponentConfig,
} from '../../helpers/utils/interfaces';
import { updateNextArrowState } from '../../helpers/splide-helper';

@Component({
  tag: 'cms-slider',
  styleUrl: 'cms-slider.scss',
  shadow: true,
})
export class CmsSlider {
  private slidersObj: Slider[] = [];
  private splideNode: HTMLElement;
  private displayCountPagesNode: HTMLElement;
  private currentPageNode: HTMLElement;
  private splideObj: Splide;
  private splideTranslations: SplideComponentConfig;

  private readonly usePagination = false;

  private readonly splideConfig = {
    type: 'slide',
    perMove: 1,
    gap: '0',
    autoWidth: true,
    rewind: false,
    slideFocus: false,
    pagination: false,
    trimSpace: true,
    autoHeight: true,
    focusableNodes: 'li',
    role: 'carousel',
  };

  private readonly displaySlidesObj = {
    '1': 'cut-single-slide',
    '2': 'cut-double-slide',
    '3': 'cut-triple-slide',
  };

  private readonly imageSizesObj = {
    '1': `(max-width: 1279px) 55vw,
    (min-width: 1280px) 752px,
    100vw`,
    '2': `(max-width: 767px) 55vw,
    (min-width: 768px) and (max-width: 1279px) 33vw,
    (min-width: 1280px) 451px,
    100vw`,
    '3': `(max-width: 767px) 55vw,
    (min-width: 768px) and (max-width: 1279px) 25vw,
    (min-width: 1280px) 320px,
    100vw`,
  };

  /**
   * Displayed how many slides in the viewport
   */
  @Prop() slidesToShow: '1' | '2' | '3';
  /**
   * Object with all sliders
   */
  @Prop() sliderElements!: Slider[] | string;
  /**
   * separator Text like 1 von 2
   */
  @Prop() paginationSeparator: string;

  /**
   * Set the tracking namespace (optional)
   */
  @Prop({ mutable: true }) namespace?: string;

  /**
   * set the accessible name
   */
  @Prop() accessibleName: string;

  /**
   * Set the translations for splide
   */
  @Prop() translations: string;

  /**
   * Parse all sliders in json
   * @param newValue
   * @private
   */

  @Watch('sliderElements')
  private objectSlidersWatcher(newValue: Slider[] | string) {
    if (newValue) {
      this.slidersObj =
        typeof newValue === 'string' ? JSON.parse(newValue) : newValue;
    }
  }

  @Element() hostElement: HTMLElement;

  private splideSlideCssMap(): CssClassMap {
    return {
      splide__slide: true,
      [this.getDisplaySlides(this.slidesToShow)]: true,
    };
  }

  private splideListCssMap(): CssClassMap {
    return {
      splide__list: true,
      [this.getDisplaySlides(this.slidesToShow)]: true,
    };
  }

  private renderSplideElement() {
    if (this.slidersObj !== undefined) {
      return this.slidersObj.map((slider: Slider) => {
        const { images, sliderText, altText } = slider;
        return (
          <li
            onFocus={() => updateNextArrowState(this.splideObj)}
            key={altText}
            class={this.splideSlideCssMap()}
            {...{
              ariaHidden: false,
              tabIndex: 0,
            }}
          >
            <div class={'splide__slide__container'}>
              {renderResponsiveImages(
                images,
                this.imageSizesObj[this.slidesToShow],
                altText,
                'slider-image',
              )}
            </div>
            {sliderText ? (
              <div class={'slider-text-wrapper'} innerHTML={sliderText} />
            ) : null}
          </li>
        );
      });
    }
  }

  private getDisplaySlides(displaySlides: string) {
    if (this.slidesToShow !== undefined) {
      return this.displaySlidesObj[displaySlides];
    }
  }

  private mountSplide() {
    if (!Build.isBrowser || this.sliderElements === undefined) {
      return;
    }
    this.splideObj = new Splide(this.splideNode, this.splideConfig);
    this.splideObj.on('ready resized visible moved', () =>
      updateNextArrowState(this.splideObj),
    );
    this.splideEventController('mounted', this.onMounted.bind(this));
    this.splideObj.mount();
    this.splideEventController('move', this.onMove.bind(this));
    this.splideEventController('visible', this.onVisible.bind(this));
  }

  private splideEventController(splideEvent: string, cb) {
    if (this.splideObj !== undefined) {
      this.splideObj.on(splideEvent, (returnedValue) => {
        cb(returnedValue);
      });
    }
  }

  private onMounted() {
    if (this.splideObj !== undefined && this.usePagination) {
      this.displayCountPagesNode.innerHTML = this.splideObj.length.toString();
      this.currentPageNode.innerHTML = (this.splideObj.index + 1).toString();
    }
  }

  private onMove(returnedValue: number) {
    if (this.splideObj !== undefined && this.usePagination) {
      this.currentPageNode.innerHTML = (returnedValue + 1).toString();
    }
  }

  private onVisible(returnedValue: Splide) {
    if (returnedValue.index === 0) {
      this.splideObj.Components.Arrows.arrows.next.classList.add('disabled');
    } else {
      this.splideObj.Components.Arrows.arrows.next.classList.remove('disabled');
    }

    if (returnedValue.index === this.splideObj.length - 1) {
      this.splideObj.Components.Arrows.arrows.next.classList.add('disabled');
    } else {
      this.splideObj.Components.Arrows.arrows.next.classList.remove('disabled');
    }
  }

  componentWillLoad() {
    if (this.sliderElements !== undefined) {
      this.objectSlidersWatcher(this.sliderElements);
    }
    if (this.translations) {
      this.splideTranslations = JSON.parse(this.translations);
    }
  }

  componentDidLoad() {
    this.splideNode = this.hostElement.shadowRoot.querySelector('.splide');
    this.displayCountPagesNode = this.hostElement.shadowRoot.querySelector(
      '.cms-slider-count-pages',
    );
    this.currentPageNode = this.hostElement.shadowRoot.querySelector(
      '.cms-slider-current-page',
    );

    if (this.sliderElements !== undefined) {
      this.mountSplide();
    }
  }

  render() {
    return (
      <Host class={'cms-slider'}>
        <div class={'cms-slider-inner'} aria-label={this.accessibleName}>
          <div class={'splide'}>
            <div class={'splide-inner'}>
              <div class='splide__arrows'>
                <cms-button
                  namespace={`${this.namespace}-arrow-left`}
                  class={'splide-arrow splide__arrow--prev'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowLeft'
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowLeftLabel}
                  focusable={false}
                />
                <cms-button
                  namespace={`${this.namespace}-arrow-right`}
                  class={'splide-arrow splide__arrow--next'}
                  theme='secondary'
                  size='button-sm'
                  type='button'
                  icon='arrowRight'
                  icon-size='sm'
                  tag-title={this.splideTranslations?.arrowRightLabel}
                  focusable={false}
                />
              </div>
              <div class={'splide__track'}>
                <ul class={this.splideListCssMap()}>
                  {this.renderSplideElement()}
                </ul>
              </div>
            </div>
          </div>
          {this.sliderElements !== undefined && this.usePagination ? (
            <div class='slider-pagination'>
              <span class='cms-slider-current-page' />
              <span class='cms-slider-of-pages'>
                {this.paginationSeparator}
              </span>
              <span class='cms-slider-count-pages' />
            </div>
          ) : null}
        </div>
      </Host>
    );
  }
}
